import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Normalizers,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  take,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IPaginationResultFrequencies,
  IRequestResult,
} from "../../../../common/interfaces";
import { ICreateMaterial } from "./interfaces/create-material.interface";
import { IUpdateMaterial } from "./interfaces/update-material.interface";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import MaterialAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/materials/material/material.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_MATERIAL_URL;
const moduleName = "material";

@Injectable({
  providedIn: "root",
})
export class MaterialService extends AbstractService<
  MaterialAttributes,
  ICreateMaterial,
  IUpdateMaterial
> {
  /** The last filter used to load the list */
  currentFilter: CombinedFilter<MaterialAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(
    filter: CombinedFilter<MaterialAttributes>,
    options?: RequestOptions
  ) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.get<IPaginationResult<MaterialAttributes>>(API_URL, {
      headers,
    });
  }

  /**
   * Obtiene materiales con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Diez objetos (OR lógico) - busca en 'code', 'name', 'externalKeys.code',
   *   'parentCode', 'barcode', 'parentBarcode', 'reference', 'parentReference',
   *   'materialGroups.name', 'materialGroups.hierarchy.name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @param materialList Filter by materialList
   * @returns Paginated data
   *
   * Search fields: code, name, externalKeys.code, parentCode, barcode, parentBarcode,
   *                reference, parentReference, materialGroups.name, materialGroups.hierarchy.name
   * Array filters: divisionId, materialBrandId, materialGroupId, status
   * Single filters: companyId, materialList
   */
  findAllWithCount(
    options: IPagination<MaterialAttributes>,
    dataToFilter: IFilters,
    materialList?
  ): Observable<IPaginationResult<MaterialAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtros de compañía, división, marca, grupo, estados, materialList.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<MaterialAttributes>[] => {
      const deletedFilter: FilterFn<MaterialAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter?.companyId
          ? andWhere('company.id' as keyof MaterialAttributes, Ops.eq(dataToFilter.companyId))
          : noop(),
        dataToFilter?.divisionId && dataToFilter.divisionId.length > 0
          ? andWhere('division.id' as keyof MaterialAttributes, Ops.in(...dataToFilter.divisionId))
          : noop(),
        dataToFilter?.materialBrandId && dataToFilter.materialBrandId.length > 0
          ? andWhere('materialBrand.code' as keyof MaterialAttributes, Ops.in(...dataToFilter.materialBrandId))
          : noop(),
        dataToFilter?.status && dataToFilter.status.length > 0
          ? andWhere('statusId', Ops.in(...dataToFilter.status))
          : noop(),
        dataToFilter?.materialGroupId && dataToFilter.materialGroupId.length > 0
          ? andWhere('materialGroups.code' as keyof MaterialAttributes, Ops.in(...dataToFilter.materialGroupId))
          : noop(),
        materialList
          ? andWhere('materialList' as keyof MaterialAttributes, Ops.eq(null))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<MaterialAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<MaterialAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchByExternalCodeQueryObject: FilterFn<MaterialAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("externalKeys.code" as keyof MaterialAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 4: Condiciones base + búsqueda por PARENT CODE (solo si hay búsqueda)
    const searchByParentCodeQueryObject: FilterFn<MaterialAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("parentCode", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 5: Condiciones base + búsqueda por BARCODE (solo si hay búsqueda)
    const searchByBarcodeQueryObject: FilterFn<MaterialAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("barcode", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 6: Condiciones base + búsqueda por PARENT BARCODE (solo si hay búsqueda)
    const searchByParentBarcodeQueryObject: FilterFn<MaterialAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("parentBarcode", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 7: Condiciones base + búsqueda por REFERENCE (solo si hay búsqueda)
    const searchByReferenceQueryObject: FilterFn<MaterialAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("reference", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 8: Condiciones base + búsqueda por PARENT REFERENCE (solo si hay búsqueda)
    const searchByParentReferenceQueryObject: FilterFn<MaterialAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("parentReference", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 9: Condiciones base + búsqueda por MATERIAL GROUPS NAME (solo si hay búsqueda)
    const searchByMaterialGroupNameQueryObject: FilterFn<MaterialAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("materialGroups.name" as keyof MaterialAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 10: Condiciones base + búsqueda por MATERIAL GROUPS HIERARCHY NAME (solo si hay búsqueda)
    const searchByMaterialGroupHierarchyNameQueryObject: FilterFn<MaterialAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("materialGroups.hierarchy.name" as keyof MaterialAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<MaterialAttributes>(
      // Expandimos los diez objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,
      ...searchByExternalCodeQueryObject,
      ...searchByParentCodeQueryObject,
      ...searchByBarcodeQueryObject,
      ...searchByParentBarcodeQueryObject,
      ...searchByReferenceQueryObject,
      ...searchByParentReferenceQueryObject,
      ...searchByMaterialGroupNameQueryObject,
      ...searchByMaterialGroupHierarchyNameQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "position",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<MaterialAttributes>>(API_URL);
  }

  findToComboBoxByGroup(
    groupId: string[]
  ): Observable<IRequestResult<MaterialAttributes>> {
    const query = buildQuery<MaterialAttributes>(
      where(
        "materialGroups._id" as keyof MaterialAttributes,
        Ops.in(...groupId)
      ),
      orderBy("name", OrderVariant.Asc)
    );
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<MaterialAttributes>>(API_URL);
  }

  findToComboBoxByGroupByCode(
    gruopCode?: string[],
    brand?: string
  ): Observable<IRequestResult<MaterialAttributes>> {
    const query = buildQuery<MaterialAttributes>(
      gruopCode && gruopCode.length > 0 ?
      where(
        "materialGroups.code" as keyof MaterialAttributes,
        Ops.in(...gruopCode)
      ) : noop(),
      brand ?
      where(
        "materialBrand.code" as keyof MaterialAttributes,
        Ops.in(...gruopCode)
      ) : noop(),
      orderBy("name", OrderVariant.Asc)
    );
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<MaterialAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(
    options: IPagination<MaterialAttributes>
  ): Observable<IPaginationResult<MaterialAttributes>> {
    const query = buildQuery<MaterialAttributes>(
      where("code", Ops.like(options.search ?? "", "i")),
      orWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere("barcode", Ops.like(options.search ?? "", "i")),
      orWhere("reference", Ops.like(options.search ?? "", "i")),
      orWhere("parentCode", Ops.like(options.search ?? "", "i")),
      orWhere("parentBarcode", Ops.like(options.search ?? "", "i")),
      orWhere("parentReference", Ops.like(options.search ?? "", "i")),
      orderBy(
        options.sort ?? "position",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<MaterialAttributes>>(API_URL);
  }

  findAllComboWithCount(
    options: IPagination<MaterialAttributes>,
    companyIds: number[],
    id
  ): Observable<IRequestResult<MaterialAttributes[]>> {
    const query = buildQuery<MaterialAttributes>(
      where("code", Ops.like(options.search ?? "", "i")),
      orWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"externalKeys.code", Ops.like(options.search ?? "", "i")),
      andAllWhere("_deleted", Ops.eq(false)),
      id
        ? andAllWhere("_id" as keyof MaterialAttributes, Ops.in(...[id]))
        : noop(), //Cnf. Lista
      companyIds && companyIds.length > 0
        ? andAllWhere(
            "company.id" as keyof MaterialAttributes,
            Ops.in(...companyIds)
          )
        : noop(),

      orderBy(
        options.sort ?? "position",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<MaterialAttributes[]>>(API_URL);
  }

  findAllComboNoCnfList(
    options: IPagination<MaterialAttributes>,
    companyIds: number[],
    id
  ): Observable<IPaginationResult<MaterialAttributes>> {
    const query = buildQuery<MaterialAttributes>(
      where("code", Ops.like(options.search ?? "", "i")),
      orWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"externalKeys.code", Ops.like(options.search ?? "", "i")),
      andAllWhere("_deleted", Ops.eq(false)),
      id
        ? andAllWhere("_id" as keyof MaterialAttributes, Ops.in(...[id]))
        : noop(), //Cnf. Lista
      companyIds && companyIds.length > 0
        ? andAllWhere(
            "company.id" as keyof MaterialAttributes,
            Ops.in(...companyIds)
          )
        : noop(),

      orderBy(
        options.sort ?? "position",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter_list?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<MaterialAttributes>>(API_URL);
  }

  /**
   * Find to related material
   * @returns Data
   */
  findToRelated(
    materialIds: string[]
  ): Observable<IRequestResult<MaterialAttributes[]>> {
    const query = buildQuery<MaterialAttributes>(
      where("_deleted", Ops.eq(false)),
      materialIds && materialIds.length > 0
        ? andAllWhere("_id" as keyof MaterialAttributes, Ops.in(...materialIds))
        : noop()
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<MaterialAttributes[]>>(API_URL);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCountShort(
    options: IPagination<MaterialAttributes>,
    code: string
  ): Observable<IPaginationResult<MaterialAttributes>> {
    const query = buildQuery<MaterialAttributes>(
      where("code", Ops.like(options.search ?? "", "i")),
      orWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"externalKeys.code", Ops.like(options.search ?? "", "i")),
      andAllWhere("_deleted", Ops.eq(false)),
      andAllWhere("materialList.code" as keyof MaterialAttributes,Ops.eq(code)),

      orderBy(
        options.sort ?? "position",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<MaterialAttributes>>(API_URL);
  }

  sortGlobal(ids) {
    const API_URL: string = apiUrl + `api/${moduleName}/sort`;
    return this.http.post<any>(API_URL, ids);
  }

  setImage(data) {
    const API_URL: string = apiUrl + `api/${moduleName}/image/set`;
    return this.http.post<any>(API_URL, data);
  }

  updateImage(id, data) {
    const API_URL: string = apiUrl + `api/${moduleName}/image/update`;
    return this.http.patch<any>(API_URL, data);
  }

  updateStatusImage(data) {
    const API_URL: string = apiUrl + `api/${moduleName}/image/status_update`;
    return this.http.patch<any>(API_URL, data);
  }

  deleteImage(id, materialId) {
    const API_URL: string =
      apiUrl + `api/${moduleName}/image/delete/${materialId}/${id}`;
    return this.http.delete<any>(API_URL);
  }

  //lista de material
  saveListMaterial(id, data) {
    const API_URL: string = apiUrl + `api/${moduleName}/material_list/${id}`;
    return this.http.patch<any>(API_URL, data);
  }

  deleteListMaterial(id) {
    const API_URL: string =
      apiUrl + `api/${moduleName}/material_list/remove/${id}`;
    return this.http.delete<any>(API_URL);
  }

  deleteAllListMaterial(id) {
    const API_URL: string =
      apiUrl + `api/${moduleName}/material_list/remove_all/${id}`;
    return this.http.delete<any>(API_URL);
  }

  saveFileMaterila(file: File, optionLoad: string) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("transform", optionLoad);
    const API_URL: string = apiUrl + `api/${moduleName}/uploadv2`;
    return this.http.post<IRequestResult<MaterialAttributes[]>>(
      API_URL,
      formData
    );
  }

  getMaterialExcelData() {
    return env.MATERIAL_LOAD_DATA;
  }

  saveFileMaterilaUM(file: File, optionLoad: string) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("transform", optionLoad);
    const API_URL: string =
      apiUrl + `api/${moduleName}/uploadv2/unit-measure-alt`;
    return this.http.post<IRequestResult<MaterialAttributes[]>>(
      API_URL,
      formData
    );
  }

  getMaterialUMExcelData() {
    return env.MATERIAL_UM_DATA;
  }

  saveFileMaterialWasteLimit(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const API_URL = `${apiUrl}api/${moduleName}/excel/waste_limit/process`;
    return this.http.post<any>(API_URL, formData);
  }

  previewFileMaterialWasteLimit(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const API_URL = `${apiUrl}api/${moduleName}/excel/waste_limit/preview`;
    return this.http.post<any>(API_URL, formData);
  }

  /**
   * Materiales cuya unidad de control logístico (`logisticUnitOfMeasureId`) es `uomId`.
   * Usado por la pantalla de unidades de medida para advertir del impacto antes de eliminar
   * una unidad. Proyecta solo `_id`, `code` y `name` para mantener la respuesta liviana.
   */
  findMaterialsByLogisticUom(
    uomId: string
  ): Observable<
    IPaginationResultFrequencies<Pick<MaterialAttributes, "_id" | "code" | "name">>
  > {
    const filter = buildQuery<MaterialAttributes>(
      select(["_id", "code", "name"]),
      take(50)
    );
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/material-with-logistic-uom?uomId=${encodeURIComponent(
        uomId
      )}&filter=${encodeURIComponent(JSON.stringify(filter))}`;
    return this.http.get<
      IPaginationResultFrequencies<Pick<MaterialAttributes, "_id" | "code" | "name">>
    >(API_URL);
  }
}
